import React, { useState } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { database, ref, query, orderByChild, equalTo, get } from '../firebase';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';

const LoginForm = () => {
  const [loginInput, setLoginInput] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1: email, 2: otp, 3: new password
  const [forgotPasswordData, setForgotPasswordData] = useState({
    email: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  const navigate = useNavigate();

  const SUPERADMIN_KEY = 'saalim@gmail_com';

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    return password.length >= 6;
  };

  const validateMobileNumber = (input) => {
    const mobileRegex = /^[0-9]{10}$/;
    return mobileRegex.test(input);
  };

  const showAlert = (message, type = 'info') => {
    alert(message);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Basic validation
    if (!loginInput.trim()) {
      showAlert('Please enter your email, username, or mobile number', 'error');
      setLoading(false);
      return;
    }
    if (!password.trim()) {
      showAlert('Please enter your password', 'error');
      setLoading(false);
      return;
    }

    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const mobileRegex = /^[0-9]{10}$/;
      let emailToUse = '';

      if (emailRegex.test(loginInput)) {
        // Direct email login
        try {
          await signInWithEmailAndPassword(auth, loginInput, password);
          showAlert('Login done successfully', 'success');
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
          return;
        } catch (authError) {
          console.error('Auth error:', authError);
          if (authError.code === 'auth/user-not-found') {
            showAlert('Please enter valid Email Id & Password.', 'error');
          } else if (authError.code === 'auth/wrong-password' || authError.code === 'auth/invalid-credential') {
            showAlert('Incorrect Password.', 'error');
          } else {
            showAlert('Please enter valid Email Id & Password.', 'error');
          }
          setLoading(false);
          return;
        }
      } else if (mobileRegex.test(loginInput)) {
        // Mobile number login - find email from database
        const employeesRef = ref(database, `users/${SUPERADMIN_KEY}/employees`);
        const mobileQuery = query(employeesRef, orderByChild('mobile'), equalTo(loginInput));
        const snapshot = await get(mobileQuery);

        if (snapshot.exists()) {
          const employeeData = Object.values(snapshot.val())[0];
          emailToUse = employeeData.email;
          
          try {
            await signInWithEmailAndPassword(auth, emailToUse, password);
            showAlert('Login done successfully', 'success');
            setTimeout(() => {
              navigate('/dashboard');
            }, 2000);
            return;
          } catch (authError) {
            console.error('Auth error:', authError);
            showAlert('Incorrect Password.', 'error');
            setLoading(false);
            return;
          }
        } else {
          showAlert('Please enter valid Email Id & Password.', 'error');
          setLoading(false);
          return;
        }
      } else {
        // Username/loginId login - find email from database
        const employeesRef = ref(database, `users/${SUPERADMIN_KEY}/employees`);
        const loginIdQuery = query(employeesRef, orderByChild('loginId'), equalTo(loginInput));
        const snapshot = await get(loginIdQuery);

        if (snapshot.exists()) {
          const employeeData = Object.values(snapshot.val())[0];
          emailToUse = employeeData.email;
          
          try {
            await signInWithEmailAndPassword(auth, emailToUse, password);
            showAlert('Login done successfully', 'success');
            setTimeout(() => {
              navigate('/dashboard');
            }, 2000);
            return;
          } catch (authError) {
            console.error('Auth error:', authError);
            showAlert('Incorrect Password.', 'error');
            setLoading(false);
            return;
          }
        } else {
          showAlert('Please enter valid Email Id & Password.', 'error');
          setLoading(false);
          return;
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      showAlert('Please enter valid Email Id & Password.', 'error');
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleShowForgotPassword = () => {
    setShowForgotPassword(true);
    setForgotPasswordStep(1);
  };

  // Step 1: Send OTP to email
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);

    // Validation
    if (!forgotPasswordData.email.trim()) {
      showAlert('Please enter your email address', 'error');
      setForgotPasswordLoading(false);
      return;
    }
    if (!validateEmail(forgotPasswordData.email)) {
      showAlert('Please enter a valid email address', 'error');
      setForgotPasswordLoading(false);
      return;
    }

    try {
      // TEMPORARY: Mock OTP functionality for testing
      // Replace this with actual API call once backend is ready
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // For testing purposes, always show success
      // In production, replace this entire try block with the commented code below
      showAlert('OTP sent to your email address. Please check your inbox.\n\nFor testing: Use OTP "123456"', 'success');
      setForgotPasswordStep(2);
      
      /* UNCOMMENT THIS FOR PRODUCTION WITH BACKEND API:
      const response = await fetch('/api/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: forgotPasswordData.email }),
      });

      const data = await response.json();
      
      if (data.ok) {
        showAlert('OTP sent to your email address. Please check your inbox.', 'success');
        setForgotPasswordStep(2);
      } else {
        showAlert(data.error || 'Failed to send OTP. Please try again.', 'error');
      }
      */
    } catch (error) {
      console.error('Send OTP error:', error);
      showAlert('Failed to send OTP. Please try again.', 'error');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);

    // Validation
    if (!forgotPasswordData.otp.trim()) {
      showAlert('Please enter the OTP', 'error');
      setForgotPasswordLoading(false);
      return;
    }
    if (forgotPasswordData.otp.length !== 6) {
      showAlert('OTP must be 6 digits', 'error');
      setForgotPasswordLoading(false);
      return;
    }

    try {
      // TEMPORARY: Mock OTP verification for testing
      // Replace this with actual API call once backend is ready
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // For testing purposes, accept "123456" as valid OTP
      if (forgotPasswordData.otp === '123456') {
        showAlert('OTP verified successfully. Now enter your new password.', 'success');
        setForgotPasswordStep(3);
      } else {
        showAlert('Invalid OTP. For testing, use "123456"', 'error');
      }
      
      /* UNCOMMENT THIS FOR PRODUCTION WITH BACKEND API:
      const response = await fetch('/api/verify-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: forgotPasswordData.email, 
          otp: forgotPasswordData.otp 
        }),
      });

      const data = await response.json();
      
      if (data.ok) {
        showAlert('OTP verified successfully. Now enter your new password.', 'success');
        setForgotPasswordStep(3);
      } else {
        showAlert(data.error || 'Invalid or expired OTP. Please try again.', 'error');
      }
      */
    } catch (error) {
      console.error('Verify OTP error:', error);
      showAlert('Failed to verify OTP. Please try again.', 'error');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);

    // Validation
    if (!forgotPasswordData.newPassword.trim()) {
      showAlert('Please enter your new password', 'error');
      setForgotPasswordLoading(false);
      return;
    }
    if (!validatePassword(forgotPasswordData.newPassword)) {
      showAlert('New password must be at least 6 characters long', 'error');
      setForgotPasswordLoading(false);
      return;
    }
    if (forgotPasswordData.newPassword !== forgotPasswordData.confirmPassword) {
      showAlert('New passwords do not match', 'error');
      setForgotPasswordLoading(false);
      return;
    }

    try {
      // TEMPORARY: Mock password reset for testing
      // Replace this with actual API call once backend is ready
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // For testing purposes, always show success
      showAlert('Password reset successfully! You can now login with your new password.', 'success');
      handleBackToLogin();
      
      /* UNCOMMENT THIS FOR PRODUCTION WITH BACKEND API:
      const response = await fetch('/api/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: forgotPasswordData.email, 
          otp: forgotPasswordData.otp,
          newPassword: forgotPasswordData.newPassword
        }),
      });

      const data = await response.json();
      
      if (data.ok) {
        showAlert('Password reset successfully! You can now login with your new password.', 'success');
        handleBackToLogin();
      } else {
        showAlert(data.error || 'Failed to reset password. Please try again.', 'error');
      }
      */
    } catch (error) {
      console.error('Reset password error:', error);
      showAlert('Failed to reset password. Please try again.', 'error');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotPasswordStep(1);
    setForgotPasswordData({
      email: '',
      otp: '',
      newPassword: '',
      confirmPassword: ''
    });
    setForgotPasswordLoading(false);
  };

  // Styles
  const styles = {
    page: {
      minHeight: '100vh',
      background: '#f4f6fb',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    },
    container: {
      background: '#fff',
      padding: '2rem 1.7rem',
      borderRadius: '1rem',
      boxShadow: '0 8px 24px rgba(35,102,209,0.10)',
      width: '100%',
      maxWidth: '400px',
      borderTop: '4px solid #2366d1',
      transition: 'box-shadow 0.3s ease',
    },
    title: {
      textAlign: 'center',
      fontWeight: 'bold',
      fontSize: '2.2rem',
      marginBottom: '1.5rem',
      color: '#2366d1',
      userSelect: 'none',
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      fontSize: '1.1rem',
      color: '#4a5568',
      fontWeight: '600',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    },
    input: {
      width: '100%',
      padding: '0.75rem 1rem',
      fontSize: '1.1rem',
      borderRadius: '0.5rem',
      border: '1.5px solid #cbd5e1',
      backgroundColor: '#f9fafb',
      color: '#1a202c',
      outline: 'none',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      marginBottom: '1.2rem',
      boxSizing: 'border-box',
      fontWeight: '600',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    },
    inputFocus: {
      borderColor: '#2366d1',
      boxShadow: '0 0 0 3px rgba(35, 102, 209, 0.15)',
      backgroundColor: '#fff',
    },
    checkboxWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '1.5rem',
    },
    checkboxSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    checkbox: {
      width: '1.2rem',
      height: '1.2rem',
      accentColor: '#2366d1',
      cursor: 'pointer',
    },
    checkboxLabel: {
      fontSize: '0.95rem',
      color: '#4a5568',
      fontWeight: '600',
      cursor: 'pointer',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    },
    forgotPassword: {
      fontSize: '0.9rem',
      color: '#2366d1',
      cursor: 'pointer',
      textDecoration: 'none',
      fontWeight: '500',
      transition: 'color 0.2s',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    },
    forgotPasswordHover: {
      color: '#1e40af',
      textDecoration: 'underline',
    },
    button: {
      width: '100%',
      padding: '0.8rem',
      fontSize: '1.1rem',
      fontWeight: '700',
      borderRadius: '0.5rem',
      border: 'none',
      cursor: 'pointer',
      color: 'white',
      background: 'linear-gradient(90deg, #2563eb 0%, #2366d1 100%)',
      boxShadow: '0 4px 12px rgba(35, 102, 209, 0.25)',
      transition: 'all 0.2s ease',
      userSelect: 'none',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    },
    buttonHover: {
      transform: 'translateY(-1px)',
      boxShadow: '0 6px 16px rgba(35, 102, 209, 0.35)',
      background: 'linear-gradient(90deg, #1d4ed8 0%, #1e40af 100%)',
    },
    buttonActive: {
      transform: 'translateY(1px)',
      boxShadow: '0 2px 8px rgba(35, 102, 209, 0.4)',
      background: 'linear-gradient(90deg, #1e40af 0%, #1d4ed8 100%)',
    },
    buttonDisabled: {
      opacity: '0.7',
      cursor: 'not-allowed',
      background: 'linear-gradient(90deg, #a5b4fc 0%, #818cf8 100%)',
      boxShadow: 'none',
      transform: 'none',
    },
    backButton: {
      width: '100%',
      padding: '0.75rem',
      fontSize: '1rem',
      fontWeight: '600',
      borderRadius: '0.5rem',
      border: '2px solid #2366d1',
      cursor: 'pointer',
      color: '#2366d1',
      backgroundColor: 'transparent',
      marginTop: '1rem',
      transition: 'all 0.2s ease',
      userSelect: 'none',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    },
    backButtonHover: {
      backgroundColor: '#2366d1',
      color: 'white',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(35, 102, 209, 0.25)',
    },
    otpInput: {
      textAlign: 'center',
      letterSpacing: '0.5rem',
      fontSize: '1.5rem',
      fontWeight: 'bold',
    },
  };

  const [inputFocus, setInputFocus] = useState({ 
    loginInput: false, 
    password: false,
    email: false,
    otp: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [buttonHover, setButtonHover] = useState(false);
  const [buttonActive, setButtonActive] = useState(false);
  const [forgotHover, setForgotHover] = useState(false);
  const [backButtonHover, setBackButtonHover] = useState(false);

  // Get current title based on state
  const getCurrentTitle = () => {
    if (showForgotPassword) {
      switch (forgotPasswordStep) {
        case 1: return 'Forgot Password';
        case 2: return 'Enter OTP';
        case 3: return 'Reset Password';
        default: return 'Forgot Password';
      }
    }
    return 'Login';
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <h2 style={styles.title}>{getCurrentTitle()}</h2>
        
        {/* Main Login Form */}
        {!showForgotPassword && (
          <form onSubmit={handleSubmit}>
            <label htmlFor="loginInput" style={styles.label}>Email </label>
            <input
              id="loginInput"
              type="text"
              value={loginInput}
              placeholder="Enter your email"
              autoComplete="username"
              required
              onChange={(e) => setLoginInput(e.target.value)}
              onFocus={() => setInputFocus((prev) => ({ ...prev, loginInput: true }))}
              onBlur={() => setInputFocus((prev) => ({ ...prev, loginInput: false }))}
              style={{
                ...styles.input,
                ...(inputFocus.loginInput ? styles.inputFocus : {}),
              }}
            />

            <label htmlFor="password" style={styles.label}>Password</label>
            <input
              id="password"
              type="password"
              value={password}
              placeholder="Enter your password"
              autoComplete="current-password"
              required
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setInputFocus((prev) => ({ ...prev, password: true }))}
              onBlur={() => setInputFocus((prev) => ({ ...prev, password: false }))}
              style={{
                ...styles.input,
                ...(inputFocus.password ? styles.inputFocus : {}),
              }}
            />

            <div style={styles.checkboxWrapper}>
              <div style={styles.checkboxSection}>
                <input
                  id="remember"
                  type="checkbox"
                  style={styles.checkbox}
                />
                <label htmlFor="remember" style={styles.checkboxLabel}>
                  Remember me
                </label>
              </div>
              <span
                style={{
                  ...styles.forgotPassword,
                  ...(forgotHover ? styles.forgotPasswordHover : {}),
                }}
                onClick={handleShowForgotPassword}
                onMouseEnter={() => setForgotHover(true)}
                onMouseLeave={() => setForgotHover(false)}
              >
                Forgot Password?
              </span>
            </div>

            <button
              type="submit"
              style={{
                ...styles.button,
                ...(loading ? styles.buttonDisabled : {}),
                ...(buttonHover && !loading ? styles.buttonHover : {}),
                ...(buttonActive && !loading ? styles.buttonActive : {}),
              }}
              disabled={loading}
              onMouseEnter={() => !loading && setButtonHover(true)}
              onMouseLeave={() => {
                setButtonHover(false);
                setButtonActive(false);
              }}
              onMouseDown={() => !loading && setButtonActive(true)}
              onMouseUp={() => setButtonActive(false)}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        )}

        {/* Forgot Password Step 1: Enter Email */}
        {showForgotPassword && forgotPasswordStep === 1 && (
          <form onSubmit={handleSendOtp}>
            <label htmlFor="email" style={styles.label}>Email</label>
            <input
              id="email"
              type="email"
              value={forgotPasswordData.email}
              placeholder="Enter your email address"
              autoComplete="email"
              required
              onChange={(e) => setForgotPasswordData(prev => ({ ...prev, email: e.target.value }))}
              onFocus={() => setInputFocus((prev) => ({ ...prev, email: true }))}
              onBlur={() => setInputFocus((prev) => ({ ...prev, email: false }))}
              style={{
                ...styles.input,
                ...(inputFocus.email ? styles.inputFocus : {}),
              }}
            />

            <button
              type="submit"
              style={{
                ...styles.button,
                ...(forgotPasswordLoading ? styles.buttonDisabled : {}),
                ...(buttonHover && !forgotPasswordLoading ? styles.buttonHover : {}),
                ...(buttonActive && !forgotPasswordLoading ? styles.buttonActive : {}),
              }}
              disabled={forgotPasswordLoading}
              onMouseEnter={() => !forgotPasswordLoading && setButtonHover(true)}
              onMouseLeave={() => {
                setButtonHover(false);
                setButtonActive(false);
              }}
              onMouseDown={() => !forgotPasswordLoading && setButtonActive(true)}
              onMouseUp={() => setButtonActive(false)}
            >
              {forgotPasswordLoading ? 'Sending OTP...' : 'Send OTP'}
            </button>

            <button
              type="button"
              onClick={handleBackToLogin}
              style={{
                ...styles.backButton,
                ...(backButtonHover ? styles.backButtonHover : {}),
              }}
              onMouseEnter={() => setBackButtonHover(true)}
              onMouseLeave={() => setBackButtonHover(false)}
            >
              Back to Login
            </button>
          </form>
        )}

        {/* Forgot Password Step 2: Enter OTP */}
        {showForgotPassword && forgotPasswordStep === 2 && (
          <form onSubmit={handleVerifyOtp}>
            <label htmlFor="otp" style={styles.label}>Enter OTP</label>
            <input
              id="otp"
              type="text"
              value={forgotPasswordData.otp}
              placeholder="Enter 6-digit OTP"
              maxLength="6"
              required
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ''); // Only digits
                setForgotPasswordData(prev => ({ ...prev, otp: value }));
              }}
              onFocus={() => setInputFocus((prev) => ({ ...prev, otp: true }))}
              onBlur={() => setInputFocus((prev) => ({ ...prev, otp: false }))}
              style={{
                ...styles.input,
                ...styles.otpInput,
                ...(inputFocus.otp ? styles.inputFocus : {}),
              }}
            />

            <button
              type="submit"
              style={{
                ...styles.button,
                ...(forgotPasswordLoading ? styles.buttonDisabled : {}),
                ...(buttonHover && !forgotPasswordLoading ? styles.buttonHover : {}),
                ...(buttonActive && !forgotPasswordLoading ? styles.buttonActive : {}),
              }}
              disabled={forgotPasswordLoading}
              onMouseEnter={() => !forgotPasswordLoading && setButtonHover(true)}
              onMouseLeave={() => {
                setButtonHover(false);
                setButtonActive(false);
              }}
              onMouseDown={() => !forgotPasswordLoading && setButtonActive(true)}
              onMouseUp={() => setButtonActive(false)}
            >
              {forgotPasswordLoading ? 'Verifying OTP...' : 'Verify OTP'}
            </button>

            <button
              type="button"
              onClick={() => setForgotPasswordStep(1)}
              style={{
                ...styles.backButton,
                ...(backButtonHover ? styles.backButtonHover : {}),
              }}
              onMouseEnter={() => setBackButtonHover(true)}
              onMouseLeave={() => setBackButtonHover(false)}
            >
              Back to Email
            </button>
          </form>
        )}

        {/* Forgot Password Step 3: Reset Password */}
        {showForgotPassword && forgotPasswordStep === 3 && (
          <form onSubmit={handleResetPassword}>
            <label htmlFor="newPassword" style={styles.label}>New Password</label>
            <input
              id="newPassword"
              type="password"
              value={forgotPasswordData.newPassword}
              placeholder="Enter your new password (min 6 characters)"
              autoComplete="new-password"
              required
              onChange={(e) => setForgotPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
              onFocus={() => setInputFocus((prev) => ({ ...prev, newPassword: true }))}
              onBlur={() => setInputFocus((prev) => ({ ...prev, newPassword: false }))}
              style={{
                ...styles.input,
                ...(inputFocus.newPassword ? styles.inputFocus : {}),
              }}
            />

            <label htmlFor="confirmPassword" style={styles.label}>Confirm New Password</label>
            <input
              id="confirmPassword"
              type="password"
              value={forgotPasswordData.confirmPassword}
              placeholder="Confirm your new password"
              autoComplete="new-password"
              required
              onChange={(e) => setForgotPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              onFocus={() => setInputFocus((prev) => ({ ...prev, confirmPassword: true }))}
              onBlur={() => setInputFocus((prev) => ({ ...prev, confirmPassword: false }))}
              style={{
                ...styles.input,
                ...(inputFocus.confirmPassword ? styles.inputFocus : {}),
              }}
            />

            <button
              type="submit"
              style={{
                ...styles.button,
                ...(forgotPasswordLoading ? styles.buttonDisabled : {}),
                ...(buttonHover && !forgotPasswordLoading ? styles.buttonHover : {}),
                ...(buttonActive && !forgotPasswordLoading ? styles.buttonActive : {}),
              }}
              disabled={forgotPasswordLoading}
              onMouseEnter={() => !forgotPasswordLoading && setButtonHover(true)}
              onMouseLeave={() => {
                setButtonHover(false);
                setButtonActive(false);
              }}
              onMouseDown={() => !forgotPasswordLoading && setButtonActive(true)}
              onMouseUp={() => setButtonActive(false)}
            >
              {forgotPasswordLoading ? 'Resetting Password...' : 'Reset Password'}
            </button>

            <button
              type="button"
              onClick={handleBackToLogin}
              style={{
                ...styles.backButton,
                ...(backButtonHover ? styles.backButtonHover : {}),
              }}
              onMouseEnter={() => setBackButtonHover(true)}
              onMouseLeave={() => setBackButtonHover(false)}
            >
              Back to Login
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginForm;